import * as IoC from 'typescript-ioc';
import {
    IApiClient,
    IApiResponse,
    IBaseRequestOptions,
    IFileRequestOptions,
    IFileResponse,
    IRequestOptions,
    IRequestUrl,
    IResponseBase,
    serviceAuthHeaderName,
} from '../../../infrastructure/api-client';
import { GET_CURRENT_USER_URL } from '../../../constants/api/users-api';
import { AuthAggregateProvider } from './auth-aggregate-provider';
import { CookieAuthAggregate, LOGOUT_PENDING_KEY } from './cookie-auth.aggregate';
import { OE_CENTRAL_DOMAIN } from '../../../constants/common.constants';

type requestFunc<TRequest, TResponse extends IResponseBase> = (url: string, parameters?: TRequest, options?: IBaseRequestOptions) => Promise<TResponse>;

/**
 * decorator, which apply authorization-specific features to all request, such as:
 * plug auth headers into all requests sent throught the underlying IApiClient
 * handle unauthorized requests (401 response)
 * handle session token rotations
 * list may change, see implementation for details
 */
class APIClientAuthDecorator extends IApiClient {
    private static readonly serviceAuthHeaderName = serviceAuthHeaderName;

    constructor(
        @IoC.Inject private readonly innerClient: IApiClient,
        @IoC.Inject private readonly authAggregateProvider: AuthAggregateProvider,
    ) {
        super();
    }

    public async delete<TResponse, TRequest = {}>(url: string, parameters?: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        const originalCall = this.innerClient.delete.bind(this.innerClient) as requestFunc<TRequest, TResponse>;
        return await this.decorateAPICall(originalCall)(url, parameters, options);
    }

    public async get<TResponse, TRequest = {}>(url: string, parameters?: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        const originalCall = this.innerClient.get.bind(this.innerClient) as requestFunc<TRequest, TResponse>;
        return await this.decorateAPICall(originalCall)(url, parameters, options);
    }

    public async getFile<TRequest = {}>(url: string, parameters?: TRequest, options?: IFileRequestOptions): Promise<IFileResponse> {
        const originalCall = this.innerClient.getFile.bind(this.innerClient) as requestFunc<TRequest, {}>;
        return await this.decorateAPICall(originalCall)(url, parameters, options);
    }

    public async post<TResponse, TRequest = {}>(url: string, payload?: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        const originalCall = this.innerClient.post.bind(this.innerClient) as requestFunc<TRequest, TResponse>;
        return await this.decorateAPICall(originalCall)(url, payload, options);
    }

    public async put<TResponse, TRequest = {}>(url: string, payload?: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        const originalCall = this.innerClient.put.bind(this.innerClient) as requestFunc<TRequest, TResponse>;
        return await this.decorateAPICall(originalCall)(url, payload, options);
    }

    public async patch<TResponse, TRequest = {}>(url: string, payload?: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        const originalCall = this.innerClient.patch.bind(this.innerClient) as requestFunc<TRequest, TResponse>;
        return await this.decorateAPICall(originalCall)(url, payload, options);
    }

    private decorateAPICall<TResponse extends IResponseBase, TRequest = {}>(apiCallFunction: requestFunc<TRequest, TResponse>)
        : requestFunc<TRequest, TResponse> {
        const authAggregate = this.authAggregateProvider.get();

        if (authAggregate == null) {
            return apiCallFunction;
        } else {
            return async (requestUrl: IRequestUrl, payload?: TRequest, options?: IRequestOptions<TResponse>) => {
                const url = typeof requestUrl === 'string' ? requestUrl : requestUrl.url;

                options = options || {};
                options.requestInit = options.requestInit || {};

                const sessionToken = await authAggregate.getSessionToken();
                if (sessionToken) {
                    // localStorage mode: inject Bearer header
                    options.requestInit.headers = options.requestInit.headers || {};
                    options.requestInit.headers[APIClientAuthDecorator.serviceAuthHeaderName]
                        = sessionToken;
                } else if (sessionToken === '') {
                    // cookie mode: send oe_session cookie only to our own backend.
                    // Relative URLs are always our backend (inner ApiClient prepends apiRoot).
                    // Absolute third-party URLs (S3, AI Gateway, etc.) never contain OE_CENTRAL_DOMAIN.
                    if (url.includes(OE_CENTRAL_DOMAIN) || !url.startsWith('http')) {
                        options.requestInit.credentials = 'include';
                    }
                }

                // perform decorated api call
                let response: TResponse;
                try {
                    response = await apiCallFunction(url, payload, options);
                } catch (e) {
                    const apiResponse = e as TResponse;
                    const responseStatus = apiResponse.response?.status;

                    if (responseStatus === 401) {
                        if (authAggregate instanceof CookieAuthAggregate) {
                            // Cookie mode: JWT inside oe_session expired — attempt silent refresh.
                            // exchangeRefreshTokenForAccessToken re-validates the new cookie on
                            // the retry below. It calls forceLogout() internally ONLY on a
                            // definitive auth failure (refresh token rejected with 401/403);
                            // a transient refresh failure (5xx, network, timeout) leaves the
                            // session intact and the LOGOUT_PENDING_KEY flag unset.
                            await authAggregate.exchangeRefreshTokenForAccessToken();
                            // Skip retry if refresh forced a logout (flag set) — the page is
                            // navigating away and the stale cookie would just get another 401.
                            if (!localStorage.getItem(LOGOUT_PENDING_KEY)) {
                                // Do NOT logout if this retry also 401s: a genuinely dead session
                                // would already have set LOGOUT_PENDING_KEY above. Reaching here
                                // means the refresh was transiently unavailable, so surface the
                                // request error and keep the session — the next request retries
                                // the refresh once the BE recovers. Tearing the session down here
                                // is what caused random logouts on a valid cookie (OEP-36665).
                                response = await apiCallFunction(url, payload, options);
                            }
                        } else {
                            await authAggregate.logout();
                        }
                    } else if (url.includes(GET_CURRENT_USER_URL) && responseStatus === 404) {
                        await authAggregate.logout();
                    } else if (responseStatus === 403) {
                        if (authAggregate instanceof CookieAuthAggregate) {
                            // 403 is returned for both anonymous (cookie deleted) and authenticated
                            // users with no permission. Probe /user/current (no @PreAuthorize) to
                            // tell them apart:
                            //   200 → valid session, real permission error → throw, component handles it
                            //   non-200 → no session (cookie deleted) → redirect to login, don't throw
                            const sessionActive = await authAggregate.probeSession();
                            if (!sessionActive) {
                                // Cookie was deleted — call logout() not authenticate():
                                // authenticate() → /fusion-auth/redirect → FA uses fusionauth.sso SSO
                                // cookie → auto re-auth → user stays in app (security issue).
                                // logout() → forceLogout() → POST /fusion-auth/logout + navigate to
                                // FA /oauth2/logout → clears fusionauth.sso → login form shown.
                                await authAggregate.logout();
                                return; // page is navigating away, suppress throw
                            }
                            // Session is valid — real permission error. Refresh the permission store
                            // so RestrictedContent re-evaluates and shows "Access Denied" instead of
                            // a generic component error (mirrors localStorage/FusionAuth flow).
                            if (!url.includes(GET_CURRENT_USER_URL)) {
                                await authAggregate.getUserPermissions();
                            }
                        } else {
                            /* [OEP-993] Forcing exchangeRefreshTokenForAccessToken as applied to the JWT secret to validate the token  */
                            if (url.includes(GET_CURRENT_USER_URL)) {
                                await authAggregate.exchangeRefreshTokenForAccessToken();
                            }
                            /* [OEP-21] we should not fetch permissions when fetching current user returns 403 (after switched from Fusion to Cognito) */
                            if (!url.includes(GET_CURRENT_USER_URL)) await authAggregate.getUserPermissions();
                        }
                        throw e;
                    } else {
                        throw e;
                    }
                }

                return response;
            };
        }
    }
}

export { APIClientAuthDecorator };